import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import api from '../utils/api';

const EnvironmentContext = createContext();

export const useEnvironment = () => {
  const context = useContext(EnvironmentContext);
  if (!context) {
    throw new Error('useEnvironment must be used within an EnvironmentProvider');
  }
  return context;
};

function pickDefaultEnvironmentId(list) {
  const prodEnv = list.find(
    (env) =>
      env?.name &&
      (env.name.toLowerCase() === 'prod' ||
        env.name.toLowerCase() === 'production')
  );
  if (prodEnv) return prodEnv.id;
  if (list.length > 0) return list[0].id;
  return null;
}

export const EnvironmentProvider = ({ children }) => {
  const { authState } = useOktaAuth();
  const [environments, setEnvironments] = useState([]);
  const [selectedEnvironmentId, setSelectedEnvironmentId] = useState(null);
  const [loading, setLoading] = useState(true);

  const isAuthResolved =
    authState != null && authState.isPending !== true;
  const isAuthenticated = Boolean(authState?.isAuthenticated);

  const fetchEnvironmentsFromApi = useCallback(async (signal) => {
    try {
      setLoading(true);
      const response = await api.get('/environments', signal ? { signal } : {});
      const raw = Array.isArray(response.data)
        ? response.data
        : response.data?.environments;
      const list = Array.isArray(raw) ? raw : [];

      setEnvironments(list);
      setSelectedEnvironmentId(pickDefaultEnvironmentId(list));
    } catch (error) {
      const aborted =
        signal?.aborted ||
        error.code === 'ERR_CANCELED' ||
        error.name === 'CanceledError';
      if (!aborted) {
        console.error('Failed to fetch environments:', error);
      }
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!isAuthResolved) {
      return;
    }
    if (!isAuthenticated) {
      setEnvironments([]);
      setSelectedEnvironmentId(null);
      setLoading(false);
      return;
    }

    const ac = new AbortController();
    void fetchEnvironmentsFromApi(ac.signal);
    return () => ac.abort();
  }, [isAuthResolved, isAuthenticated, fetchEnvironmentsFromApi]);

  const selectedEnvironment = environments.find(env => env.id === selectedEnvironmentId);

  const refetchEnvironments = useCallback(async () => {
    if (!isAuthenticated) return;
    await fetchEnvironmentsFromApi();
  }, [isAuthenticated, fetchEnvironmentsFromApi]);

  const value = {
    environments,
    selectedEnvironmentId,
    setSelectedEnvironmentId,
    selectedEnvironment,
    loading,
    refetchEnvironments,
  };

  return (
    <EnvironmentContext.Provider value={value}>
      {children}
    </EnvironmentContext.Provider>
  );
};

